import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const PurchaseOrderCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [procurementItems, setProcurementItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const units = ['pcs', 'kg', 'lbs', 'ft', 'm', 'sqft', 'sqm', 'gal', 'L', 'box', 'roll', 'sheet'];

  const [formData, setFormData] = useState({
    poNumber: '',
    vendor: '',
    project: '',
    requestedBy: user?.name || '',
    items: [],
    deliveryAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    expectedDeliveryDate: null,
    paymentTerms: '',
    notes: '',
    urgency: 'normal'
  });

  const [newItem, setNewItem] = useState({
    item: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    unit: 'pcs'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [vendorsRes, itemsRes, companiesRes, projectsRes] = await Promise.all([
          api.get('/vendors?status=Active'),
          api.get('/procurement-items'),
          api.get('/companies?limit=100'),
          api.get('/projects?limit=100')
        ]);
        // Prefer active vendors; if none returned (case mismatch), fall back to all vendors
        const initialVendors = vendorsRes.data || [];
        if (Array.isArray(initialVendors) && initialVendors.length > 0) {
          setVendors(initialVendors);
        } else {
          try {
            const allVendorsRes = await api.get('/vendors');
            const allVendors = allVendorsRes.data || [];
            const activeOnly = allVendors.filter((v) => (v.status || '').toLowerCase() === 'active');
            setVendors(activeOnly);
          } catch (fallbackErr) {
            console.warn('Fallback vendor fetch failed:', fallbackErr);
            setVendors([]);
          }
        }
        setProcurementItems(itemsRes.data || []);
        const list = (companiesRes.data && companiesRes.data.data) || companiesRes.data || [];
        setCompanies(Array.isArray(list) ? list : []);
        const projectsListRaw = (projectsRes.data && (projectsRes.data.data || projectsRes.data.projects)) || projectsRes.data || [];
        setProjects(Array.isArray(projectsListRaw) ? projectsListRaw : []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load vendors/items/companies/projects');
      }
    };
    loadData();
  }, []);

  const effectiveVendors = selectedCompany
    ? vendors.filter((v) => (v.companyName || '').trim().toLowerCase() === selectedCompany.trim().toLowerCase())
    : vendors;

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleAddItem = () => {
    if (newItem.item && newItem.quantity > 0) {
      const item = {
        ...newItem,
        totalPrice: newItem.quantity * newItem.unitPrice
      };
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, item]
      }));
      setNewItem({ item: '', description: '', quantity: 1, unitPrice: 0, unit: 'pcs' });
    }
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    return (formData.items || []).reduce((sum, it) => sum + (it.quantity * it.unitPrice), 0);
  };

  const handleSubmit = async () => {
    try {
      // Client-side validation for required fields
      const errors = {};
      if (!formData.project) errors.project = 'Project is required';
      if (!formData.vendor) errors.vendor = 'Vendor is required';
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        setError(errors.vendor || errors.project || 'Please fix validation errors');
        return;
      }

      setFormErrors({});
      await api.post('/purchase-orders', formData);
      setSuccess('Purchase order created successfully');
      navigate('/purchase-orders');
    } catch (err) {
      console.error('Error saving purchase order:', err);
      const message = err?.response?.data?.message || (Array.isArray(err?.response?.data?.errors) ? err.response.data.errors.join(', ') : 'Failed to save purchase order');
      setError(message);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Create Purchase Order</Typography>
        <Button variant="outlined" onClick={() => navigate('/purchase-orders')}>Back to Purchase Orders</Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Number"
                value={formData.poNumber}
                onChange={(e) => handleInputChange('poNumber', e.target.value)}
                placeholder="Auto-generated if empty"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={formData.project}
                  label="Project"
                  onChange={(e) => handleInputChange('project', e.target.value)}
                  required
                >
                  {projects.map((p) => (
                    <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                  ))}
                </Select>
                {formErrors.project && (
                  <FormHelperText error>{formErrors.project}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => option.name || ''}
                value={companies.find((c) => c.name === selectedCompany) || null}
                onChange={(event, newValue) => {
                  const companyName = (newValue?.name || '').trim();
                  setSelectedCompany(companyName);
                  const matchedVendors = vendors.filter((v) => (v.companyName || '').trim().toLowerCase() === companyName.toLowerCase());
                  setFormData(prev => ({ ...prev, vendor: matchedVendors[0]?._id || '' }));
                  // Clear vendor field error if we auto-populated
                  setFormErrors(prev => ({ ...prev, vendor: matchedVendors[0]?._id ? undefined : prev.vendor }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Company Name" placeholder="Select company to filter vendors" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={formData.vendor}
                  label="Vendor"
                  onChange={(e) => handleInputChange('vendor', e.target.value)}
                  required
                >
                  {effectiveVendors.map((v) => (
                    <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
                  ))}
                </Select>
                {formErrors.vendor && (
                  <FormHelperText error>{formErrors.vendor}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Requested By"
                value={formData.requestedBy}
                onChange={(e) => handleInputChange('requestedBy', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6">Delivery Address</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.deliveryAddress.street}
                onChange={(e) => handleInputChange('deliveryAddress.street', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.deliveryAddress.city}
                onChange={(e) => handleInputChange('deliveryAddress.city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.deliveryAddress.state}
                onChange={(e) => handleInputChange('deliveryAddress.state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.deliveryAddress.zipCode}
                onChange={(e) => handleInputChange('deliveryAddress.zipCode', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Expected Delivery"
                value={formData.expectedDeliveryDate}
                onChange={(date) => handleInputChange('expectedDeliveryDate', date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Terms"
                value={formData.paymentTerms}
                onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6">Items</Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={procurementItems}
                getOptionLabel={(option) => option.name || ''}
                value={procurementItems.find(item => item._id === newItem.item) || null}
                onChange={(event, newValue) => {
                  setNewItem(prev => ({
                    ...prev,
                    item: newValue?._id || '',
                    description: newValue?.description || '',
                    unitPrice: newValue?.pricing?.currentPrice || newValue?.currentPrice || 0
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Item" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={newItem.quantity}
                onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={newItem.unit}
                  label="Unit"
                  onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                >
                  {units.map((u) => (
                    <MenuItem key={u} value={u}>{u}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={newItem.unitPrice}
                onChange={(e) => setNewItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button fullWidth variant="contained" onClick={handleAddItem} sx={{ height: '56px' }}>Add</Button>
            </Grid>

            <Grid item xs={12}>
              {formData.items.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((it, index) => (
                        <TableRow key={index}>
                          <TableCell>{it.item}</TableCell>
                          <TableCell>{it.description}</TableCell>
                          <TableCell>{it.quantity}</TableCell>
                          <TableCell>{it.unit}</TableCell>
                          <TableCell>${it.unitPrice.toFixed(2)}</TableCell>
                          <TableCell>${(it.quantity * it.unitPrice).toFixed(2)}</TableCell>
                          <TableCell>
                            <Button color="error" size="small" onClick={() => handleRemoveItem(index)}>Remove</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={5} align="right">
                          <Typography variant="subtitle1">Total:</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle1">${calculateTotal().toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary" align="center" sx={{ py: 2 }}>
                  No items added yet
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="outlined" sx={{ mr: 2 }} onClick={() => navigate('/purchase-orders')}>Cancel</Button>
              <Button variant="contained" onClick={handleSubmit}>Create Purchase Order</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseOrderCreate;